import { ICameraCCTV } from '@api/models';
import { createCctvCamera } from '@api/services/cctv-camera.service';
import { useMutation } from '@tanstack/react-query';
import { timezoneList } from 'modules/shared/helpers/shared.constants';
import moment from 'moment';
import { useMemo } from 'react';
import * as Yup from 'yup';

type ChannelInput = {
    number: string;
    // streamingUrl: string;
    hlsUrl?: string;
    webrtcUrl?: string;
    folder: string;
    ptzControl: {
        isAvailable: boolean;
        deviceId?: string;
    };
};

const useCctvCameraAddForm = (clientId: string, projectId: string) => {
    const addCctvCameraMutation = useMutation((payload: any) => createCctvCamera(clientId, projectId, payload));

    const schema = Yup.object().shape({
        name: Yup.string().trim().required('This field is required'),
        installationDate: Yup.string().trim().required('This field is required'),
        qLiveUnit: Yup.string().trim().required('This field is required'),
        storage: Yup.string().required('This field is required'),
        timezone: Yup.string().trim().required('This field is required'),
        isPrimary: Yup.boolean().required(),
        location: Yup.object().shape({
            latitude: Yup.string().trim(),
            longitude: Yup.string().trim(),
        }),
        channels: Yup.array()
            .of(
                Yup.object({
                    number: Yup.string().trim().required('Channel number is required'),
                    // streamingUrl: Yup.string().trim().required('Streaming URL is required'),
                    hlsUrl: Yup.string().trim().required('This field is required'),
                    webrtcUrl: Yup.string().trim().nullable(),
                    // folder: Yup.string().trim().nullable(),
                    ptzControl: Yup.object({
                        isAvailable: Yup.boolean().required('This field is required'),
                        deviceId: Yup.string()
                            .trim()
                            .when('isAvailable', {
                                is: true,
                                then: (s) => s.required('Device ID is required when PTZ is available'),
                                otherwise: (s) => s.notRequired(),
                            }),
                    }),
                })
            )
            .min(1, 'Add at least one channel'),
    });

    const initialValues = {
        name: '',
        installationDate: moment(),
        qLiveUnit: '',
        storage: '',
        isPrimary: false,
        timezone: 'Asia/Dubai',
        location: { latitude: '', longitude: '' },
        channels: [
            {
                number: '',
                // streamingUrl: '',
                hlsUrl: '',
                webrtcUrl: '',
                folder: '',
                ptzControl: { isAvailable: false, deviceId: '' },
            } as ChannelInput,
        ],
    };

    const submit = async (
        values: ICameraCCTV & {
            channels: Array<ChannelInput>;
        }
    ) => addCctvCameraMutation.mutateAsync(values);

    const timezoneOption = useMemo(() => {
        return [
            {
                label: 'Select Timezone',
                value: '',
            },
            ...timezoneList.map((tz) => ({
                label: tz.label,
                value: tz.value,
            })),
        ];
    }, []);

    return {
        schema,
        initialValues,
        submit,
        isSubmitting: addCctvCameraMutation.isLoading,
        timezoneOption,
    };
};

export default useCctvCameraAddForm;
